import Phaser from 'phaser';
import { FRAMES, DISCS, DRIVERS, buildCustomBey } from '../game/parts.ts';
import { setCustomBey, pickOpponent, getBeyDef, type SpinDir } from '../game/stats.ts';
import { specialFor } from '../game/specials.ts';
import { newRun, type GameMode } from '../game/tournament.ts';
import { sound } from '../audio/sound.ts';
import { WORLD } from '../world.ts';
import { backdropGlow } from './TitleScene.ts';

const STAT_KEYS = ['attack', 'defense', 'stamina', 'weight', 'speed', 'durability'] as const;

/** Build-Your-Own-Beyblade: pick a Frame + Disc + Driver, preview, then fight. */
export class WorkshopScene extends Phaser.Scene {
  private mode: GameMode = 'tournament';
  private hard = false;
  private slot = 0; // 0 frame, 1 disc, 2 driver, 3 spin
  private static readonly SLOTS = 4;
  private frameIdx = 0;
  private discIdx = 1;
  private driverIdx = 3;
  private spinDir: SpinDir = 'right';

  private face!: Phaser.GameObjects.Sprite;
  private nameText!: Phaser.GameObjects.Text;
  private specialText!: Phaser.GameObjects.Text;
  private rows!: Phaser.GameObjects.Text[];
  private statsG!: Phaser.GameObjects.Graphics;
  private statValues!: Phaser.GameObjects.Text[];

  constructor() {
    super('Workshop');
  }

  create(data: { mode?: GameMode; hard?: boolean }): void {
    this.mode = data?.mode ?? 'tournament';
    this.hard = data?.hard ?? false;
    this.slot = 0;
    this.frameIdx = 0;
    this.discIdx = 1;
    this.driverIdx = 3;
    this.spinDir = 'right';
    this.cameras.main.setBackgroundColor('#05060a');
    backdropGlow(this);

    this.add
      .text(WORLD.width / 2, 48, 'WORKSHOP', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '44px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    // Preview bey.
    this.face = this.add.sprite(200, 230, 'bey-phoenix-face').setDisplaySize(180, 180);
    this.nameText = this.add
      .text(200, 340, '', { fontFamily: 'Trebuchet MS, sans-serif', fontSize: '26px', fontStyle: 'bold', color: '#fff' })
      .setOrigin(0.5);
    this.specialText = this.add
      .text(200, 372, '', { fontFamily: 'Trebuchet MS, sans-serif', fontSize: '16px', color: '#5ad6ff' })
      .setOrigin(0.5);

    // Part rows.
    this.rows = ['Frame', 'Disc', 'Driver', 'Spin'].map((label, i) =>
      this.add
        .text(440, 150 + i * 64, label, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '24px',
          color: '#cfe0ff',
        })
        .setOrigin(0, 0.5),
    );

    // Stat bars.
    this.statsG = this.add.graphics();
    this.statValues = STAT_KEYS.map((_k, i) =>
      this.add
        .text(440 + 360, 380 + i * 28, '0', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '17px',
          fontStyle: 'bold',
          color: '#fff',
        })
        .setOrigin(1, 0),
    );
    STAT_KEYS.forEach((k, i) =>
      this.add
        .text(440, 380 + i * 28, k, { fontFamily: 'Trebuchet MS, sans-serif', fontSize: '16px', color: '#9fb4d8' })
        .setOrigin(0, 0),
    );

    this.add
      .text(WORLD.width / 2, WORLD.height - 36, '▲▼ slot    ◄► change part    ENTER  build & fight', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: '#8aa0c8',
      })
      .setOrigin(0.5);

    const kb = this.input.keyboard!;
    kb.on('keydown-UP', () => this.moveSlot(-1));
    kb.on('keydown-DOWN', () => this.moveSlot(1));
    kb.on('keydown-LEFT', () => this.changePart(-1));
    kb.on('keydown-RIGHT', () => this.changePart(1));
    kb.on('keydown-ENTER', () => this.confirm());

    this.refresh();
  }

  private moveSlot(d: number): void {
    this.slot = (this.slot + d + WorkshopScene.SLOTS) % WorkshopScene.SLOTS;
    sound.blip();
    this.refresh();
  }

  private changePart(d: number): void {
    if (this.slot === 0) this.frameIdx = (this.frameIdx + d + FRAMES.length) % FRAMES.length;
    else if (this.slot === 1) this.discIdx = (this.discIdx + d + DISCS.length) % DISCS.length;
    else if (this.slot === 2) this.driverIdx = (this.driverIdx + d + DRIVERS.length) % DRIVERS.length;
    else this.spinDir = this.spinDir === 'right' ? 'left' : 'right';
    sound.blip();
    this.refresh();
  }

  private refresh(): void {
    const frame = FRAMES[this.frameIdx];
    const disc = DISCS[this.discIdx];
    const driver = DRIVERS[this.driverIdx];
    const def = buildCustomBey(frame.id, disc.id, driver.id, this.spinDir);

    this.face.setTexture(`bey-${frame.id}-face`).setDisplaySize(180, 180);
    this.nameText.setText(def.name);
    const sp = specialFor(def.type);
    const spinGlyph = this.spinDir === 'right' ? '↻ Right-spin' : '↺ Left-spin';
    this.specialText.setText(`★ ${sp.name}   ${spinGlyph}`);

    const frameName = getBeyDef(frame.id).name;
    const labels = [
      `Frame:  ${frameName}  (${def.type})`,
      `Disc:   ${disc.name}`,
      `Driver: ${driver.name}`,
      `Spin:   ${this.spinDir === 'right' ? '↻ Right' : '↺ Left'}`,
    ];
    this.rows.forEach((row, i) => {
      const active = i === this.slot;
      row.setText(`${active ? '◄ ' : '  '}${labels[i]}${active ? ' ►' : ''}`);
      row.setColor(active ? '#ffd23b' : '#cfe0ff');
      row.setFontStyle(active ? 'bold' : 'normal');
    });

    // Stat bars.
    const g = this.statsG;
    g.clear();
    const barX = 560;
    const barW = 200;
    STAT_KEYS.forEach((k, i) => {
      const v = def.stats[k];
      const y = 382 + i * 28;
      g.fillStyle(0x222a38, 1);
      g.fillRoundedRect(barX, y, barW, 12, 3);
      g.fillStyle(def.color, 1);
      g.fillRoundedRect(barX, y, (barW * v) / 10, 12, 3);
      this.statValues[i].setText(String(v));
    });
  }

  private confirm(): void {
    const frame = FRAMES[this.frameIdx];
    const def = buildCustomBey(
      frame.id,
      DISCS[this.discIdx].id,
      DRIVERS[this.driverIdx].id,
      this.spinDir,
    );
    setCustomBey(def);
    sound.special();
    if (this.mode === 'tournament') {
      this.registry.set('run', newRun('custom', this.hard));
      this.scene.start('Tournament', {});
    } else {
      const opp = pickOpponent('custom');
      this.scene.start('Battle', {
        playerId: 'custom',
        opponentId: opp.id,
        difficulty: 0.7,
        mode: 'exhibition',
      });
    }
  }
}
